import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixPaymentsDisputeHistoryRecordsV1DisputeHistoryRecordServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath:
          '/payments/dispute-history-records/v1/bulk/dispute-history-records',
        destPath: '/v1/bulk/dispute-history-records',
      },
      {
        srcPath: '/payments/dispute-history-records/v1/dispute-history-records',
        destPath: '/v1/dispute-history-records',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath:
          '/payments/dispute-history-records/v1/bulk/dispute-history-records',
        destPath: '/v1/bulk/dispute-history-records',
      },
      {
        srcPath: '/payments/dispute-history-records/v1/dispute-history-records',
        destPath: '/v1/dispute-history-records',
      },
    ],
    _: [
      {
        srcPath:
          '/payments/dispute-history-records/v1/bulk/dispute-history-records',
        destPath: '/v1/bulk/dispute-history-records',
      },
      {
        srcPath: '/payments/dispute-history-records/v1/dispute-history-records',
        destPath: '/v1/dispute-history-records',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_payments_dispute-history-records';

/** Retrieves a dispute history record. */
export function getDisputeHistoryRecord(
  payload: object
): RequestOptionsFactory<any> {
  function __getDisputeHistoryRecord({ host }: any) {
    const metadata = {
      entityFqdn:
        'wix.payments.dispute_history_records.v1.dispute_history_record',
      method: 'GET' as any,
      methodFqn:
        'wix.payments.dispute_history_records.v1.DisputeHistoryRecordService.GetDisputeHistoryRecord',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsDisputeHistoryRecordsV1DisputeHistoryRecordServiceUrl(
        {
          protoPath: '/v1/dispute-history-records/{disputeHistoryRecordId}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'disputeHistoryRecord.createdDate' },
              { path: 'disputeHistoryRecord.updatedDate' },
              { path: 'disputeHistoryRecord.dueDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getDisputeHistoryRecord;
}

/**
 * Creates a query to retrieve a list of dispute history records.
 *
 * The Query Dispute History Records method builds a query to retrieve a list of dispute history records and returns a `DisputeHistoryRecordsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the `find()` method.
 *
 * You can refine the query by chaining `DisputeHistoryRecordsQueryBuilder` methods onto the query. `DisputeHistoryRecordsQueryBuilder` methods enable you to filter, sort, and control the results that Query Dispute History Records returns.
 *
 * Query Dispute History Records has a default paging limit of 50, which you can override.
 *
 *  For a full description of the item object, see the object returned for the `items` property in `DisputeHistoryRecordsQueryResult`.
 */
export function queryDisputeHistoryRecords(
  payload: object
): RequestOptionsFactory<any> {
  function __queryDisputeHistoryRecords({ host }: any) {
    const metadata = {
      entityFqdn:
        'wix.payments.dispute_history_records.v1.dispute_history_record',
      method: 'GET' as any,
      methodFqn:
        'wix.payments.dispute_history_records.v1.DisputeHistoryRecordService.QueryDisputeHistoryRecords',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsDisputeHistoryRecordsV1DisputeHistoryRecordServiceUrl(
        { protoPath: '/v1/dispute-history-records/query', data: payload, host }
      ),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'disputeHistoryRecords.createdDate' },
              { path: 'disputeHistoryRecords.updatedDate' },
              { path: 'disputeHistoryRecords.dueDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixPaymentsDisputeHistoryRecordsV1DisputeHistoryRecordServiceUrl(
            {
              protoPath: '/v1/dispute-history-records/query',
              data: payload,
              host,
            }
          ),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryDisputeHistoryRecords;
}

/**
 * Updates tags on multiple dispute history records simultaneously using a list of dispute history record IDs.
 * If a tag appears in both assign and unassign lists, it will be assigned.
 */
export function bulkUpdateDisputeHistoryRecordTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateDisputeHistoryRecordTags({ host }: any) {
    const metadata = {
      entityFqdn:
        'wix.payments.dispute_history_records.v1.dispute_history_record',
      method: 'POST' as any,
      methodFqn:
        'wix.payments.dispute_history_records.v1.DisputeHistoryRecordService.BulkUpdateDisputeHistoryRecordTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsDisputeHistoryRecordsV1DisputeHistoryRecordServiceUrl(
        {
          protoPath: '/v1/bulk/dispute-history-records/update-tags',
          data: payload,
          host,
        }
      ),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateDisputeHistoryRecordTags;
}

/**
 * Asynchronously updates tags on multiple dispute history records based on filter criteria.
 * Returns a job ID that can be used to track the operation status. If a tag appears in both assign and unassign lists, it will be assigned.
 */
export function bulkUpdateDisputeHistoryRecordTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateDisputeHistoryRecordTagsByFilter({ host }: any) {
    const metadata = {
      entityFqdn:
        'wix.payments.dispute_history_records.v1.dispute_history_record',
      method: 'POST' as any,
      methodFqn:
        'wix.payments.dispute_history_records.v1.DisputeHistoryRecordService.BulkUpdateDisputeHistoryRecordTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsDisputeHistoryRecordsV1DisputeHistoryRecordServiceUrl(
        {
          protoPath: '/v1/bulk/dispute-history-records/update-tags-by-filter',
          data: payload,
          host,
        }
      ),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateDisputeHistoryRecordTagsByFilter;
}
